import { useQuery } from '@tanstack/react-query';
import { fetchDemographics } from './api';

const numFmt = new Intl.NumberFormat('it-IT');

/**
 * Pubblico reale della pagina: barre orizzontali per fascia d'età,
 * split donne/uomini. Dati dal breakdown reach EU di Meta (già in archivio).
 */
export default function Demographics({ pageId }: { pageId: string }) {
  const demo = useQuery({
    queryKey: ['demographics', pageId],
    queryFn: () => fetchDemographics(pageId),
    staleTime: 5 * 60_000,
  });

  if (demo.isPending) return <p className="demo-note">Carico il pubblico reale…</p>;
  if (demo.isError) return <p className="demo-note">Breakdown non disponibile.</p>;

  const buckets = (demo.data ?? []).filter((b) => b.male + b.female + b.unknown > 0);
  if (buckets.length === 0) {
    return (
      <p className="demo-note">
        Nessun breakdown demografico per questa pagina (annunci ingeriti prima
        dell'aggiornamento: rifai una ricerca live per popolarli).
      </p>
    );
  }

  const totalM = buckets.reduce((s, b) => s + b.male, 0);
  const totalF = buckets.reduce((s, b) => s + b.female, 0);
  const total = totalM + totalF + buckets.reduce((s, b) => s + b.unknown, 0);
  const max = Math.max(...buckets.map((b) => b.male + b.female + b.unknown));

  return (
    <div className="demo">
      <p className="demo-head">
        Pubblico reale raggiunto: {numFmt.format(total)} persone ·{' '}
        {Math.round((totalF / (totalM + totalF || 1)) * 100)}% donne,{' '}
        {Math.round((totalM / (totalM + totalF || 1)) * 100)}% uomini
      </p>
      {buckets.map((b) => {
        const rowTotal = b.male + b.female + b.unknown;
        return (
          <div key={b.ageRange} className="demo-row">
            <span className="demo-age">{b.ageRange}</span>
            <span className="demo-bar" style={{ width: `${(rowTotal / max) * 100}%` }}>
              <span className="demo-f" style={{ width: `${(b.female / rowTotal) * 100}%` }} />
              <span className="demo-m" style={{ width: `${(b.male / rowTotal) * 100}%` }} />
            </span>
            <span className="demo-n">{numFmt.format(rowTotal)}</span>
          </div>
        );
      })}
      <p className="demo-legend">
        <span className="demo-key demo-f" /> donne · <span className="demo-key demo-m" /> uomini
      </p>
    </div>
  );
}
